





























































import { Component, Mixins, Ref, Watch } from "vue-property-decorator";
import { Getter, Mutation } from "vuex-class";

import DetailsModal from "@/components/DetailsModal.vue";
import VSwitchButton from "@/components/VSwitchButton.vue";
import ConfirmModal from "@/components/ConfirmModal.vue";

import AutomaticReportMixin from "@/mixins/http/AutomaticReportMixin";
import { ReportBuilderTabKey } from "@/mixins/ReportBuilder/ReportBuilderMixin";
import { AdcontrolProfile, AutomaticReport } from "@/models";
import { describeGranularity } from "@/components/Agencies/ReportBuilder/automaticReport";
import ToastInvoker, { responseErrorMessage } from "@/utils/ToastInvoker";

import SettingsList from "@/components/Settings/SettingsList.vue";
import {
  SettingsListAction,
  SettingsListActionEvent,
  SettingsListColumn,
} from "@/components/Settings/settingsListTypes";
import AutomaticReportDetailsTable from "@/components/Settings/AutomaticReportDetailsTable.vue";
import AutomaticReportJobsTable from "@/components/Settings/AutomaticReportJobsTable.vue";

@Component({
  components: {
    SettingsList,
    DetailsModal,
    VSwitchButton,
    ConfirmModal,
    AutomaticReportDetailsTable,
    AutomaticReportJobsTable,
  },
})
export default class AutomaticReportsList extends Mixins(AutomaticReportMixin) {
  @Getter("profile") readonly profile!: AdcontrolProfile;

  @Getter("automaticReportsCache/cachedAutomaticReports")
  readonly cachedAutomaticReports!: (slug: string) => AutomaticReport[] | null;
  @Mutation("automaticReportsCache/setCachedAutomaticReports")
  readonly setCachedAutomaticReports!: (payload: {
    slug: string;
    automaticReports: AutomaticReport[];
  }) => void;
  @Mutation("automaticReportsCache/invalidateAutomaticReportsCache")
  readonly invalidateAutomaticReportsCache!: () => void;
  @Mutation("reportTemplatesCache/invalidateTemplatesCache")
  readonly invalidateTemplatesCache!: () => void;

  @Ref("list") readonly list?: SettingsList;
  @Ref("detailsModal") readonly detailsModal!: DetailsModal;
  @Ref("confirmModal") readonly confirmModal!: ConfirmModal;

  automaticReports: AutomaticReport[] = [];
  loading = false;
  error = false;
  selectedAutomaticReport: AutomaticReport | null = null;
  deleting = false;
  togglingAutomaticReportId: string | null = null;

  private fetching = false;

  readonly columns: SettingsListColumn<AutomaticReport>[] = [
    { key: "active", label: "attivo", col: "col-2" },
    {
      key: "name",
      label: "nome",
      col: "col-4",
      cellClass: "text-truncate",
      value: (automaticReport) => this.displayName(automaticReport),
    },
    {
      key: "granularity",
      label: "dimensione estrazione",
      col: "col-3",
      cellClass: "text-truncate text-muted",
      value: (automaticReport) => this.dimensionLabel(automaticReport),
    },
  ];

  readonly actions: SettingsListAction<AutomaticReport>[] = [
    {
      key: "details",
      icon: "eye",
      iconV2: true,
      title: "Dettagli",
      run: (automaticReport) => this.openDetails(automaticReport),
    },
    {
      key: "edit",
      icon: "edit-02",
      iconV2: true,
      title: "Modifica",
      run: (automaticReport) => this.onEdit(automaticReport),
    },
    {
      key: "delete",
      icon: "trash-01",
      iconV2: true,
      title: "Elimina",
      variant: "danger",
      run: (automaticReport) => this.onDelete(automaticReport),
    },
  ];

  get hasProfile(): boolean {
    return !!(this.profile && this.profile.slug);
  }

  @Watch("profile.slug", { immediate: true })
  onProfileChange() {
    this.list?.reset();
    if (!this.hasProfile) {
      this.automaticReports = [];
      return;
    }
    const cachedForProfile = this.cachedAutomaticReports(this.profile.slug);
    if (cachedForProfile) {
      this.automaticReports = cachedForProfile;
      return;
    }
    this.fetchAutomaticReports();
  }

  refresh(): Promise<void> {
    return this.fetchAutomaticReports();
  }

  async fetchAutomaticReports() {
    if (!this.hasProfile) {
      this.automaticReports = [];
      return;
    }
    if (this.fetching) return;
    this.fetching = true;
    this.loading = true;
    this.error = false;
    try {
      this.automaticReports = await this.indexAutomaticReports(
        this.profile.slug,
        { kind: "competitor" }
      );
      this.setCachedAutomaticReports({
        slug: this.profile.slug,
        automaticReports: this.automaticReports,
      });
    } catch (e) {
      this.error = true;
      this.automaticReports = [];
      this.invalidateAutomaticReportsCache();
    } finally {
      this.loading = false;
      this.fetching = false;
    }
  }

  displayName(automaticReport: AutomaticReport): string {
    return automaticReport.name || `Report #${automaticReport.id}`;
  }

  dimensionLabel(automaticReport: AutomaticReport): string {
    return describeGranularity(automaticReport);
  }

  onAction({ key, item }: SettingsListActionEvent<AutomaticReport>) {
    this.actions.find((a) => a.key === key)?.run(item);
  }

  // --- Active toggle -------------------------------------------------------

  async onToggleActive(automaticReport: AutomaticReport) {
    this.togglingAutomaticReportId = automaticReport.id;
    try {
      const updated = await this.toggleActiveAutomaticReport(
        this.profile.slug,
        automaticReport.id
      );
      automaticReport.active =
        updated && typeof updated.active === "boolean"
          ? updated.active
          : !automaticReport.active;
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
    } finally {
      this.togglingAutomaticReportId = null;
    }
  }

  // --- Multi-delete --------------------------------------------------------

  async onBulkDelete(automaticReportIds: string[]) {
    if (!automaticReportIds.length) return;
    const confirmed = await this.confirmModal.confirm(
      `Eliminare ${automaticReportIds.length} report automatici? L'operazione non è reversibile.`
    );
    if (!confirmed) return;

    this.deleting = true;
    try {
      await this.bulkDestroyAutomaticReports(
        this.profile.slug,
        automaticReportIds
      );
      this.automaticReports = this.automaticReports.filter(
        (ar) => !automaticReportIds.includes(ar.id)
      );
      this.setCachedAutomaticReports({
        slug: this.profile.slug,
        automaticReports: this.automaticReports,
      });
      this.invalidateTemplatesCache();
      this.list?.exitSelectionMode();
      ToastInvoker.successToast("Report automatici eliminati.");
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
    } finally {
      this.deleting = false;
    }
  }

  // --- Single delete -------------------------------------------------------

  async onDelete(automaticReport: AutomaticReport) {
    const confirmed = await this.confirmModal.confirm(
      `Eliminare il report automatico "${this.displayName(
        automaticReport
      )}"? L'operazione non è reversibile.`
    );
    if (!confirmed) return;

    try {
      await this.destroyAutomaticReport(this.profile.slug, automaticReport.id);
      this.automaticReports = this.automaticReports.filter(
        (ar) => ar.id !== automaticReport.id
      );
      this.setCachedAutomaticReports({
        slug: this.profile.slug,
        automaticReports: this.automaticReports,
      });
      this.invalidateTemplatesCache();
      ToastInvoker.successToast(
        `Report "${this.displayName(automaticReport)}" eliminato.`
      );
    } catch (error) {
      ToastInvoker.errorToast(responseErrorMessage(error), error);
    }
  }

  // --- Details -------------------------------------------------------------

  openDetails(automaticReport: AutomaticReport) {
    this.selectedAutomaticReport = automaticReport;
    this.$nextTick(() => this.detailsModal.show());
  }

  closeDetails() {
    this.detailsModal.hide();
  }

  // --- Navigation (new / edit) --------------------------------------------

  private goToAutomaticTab(extraQuery: Record<string, string> = {}) {
    const query: Record<string, string> = {
      tab: ReportBuilderTabKey.AutomaticReport,
      ...extraQuery,
    };
    if (this.profile?.slug) query.profile = this.profile.slug;
    this.$router.push({ name: "custom-report", query });
  }

  onNew() {
    this.goToAutomaticTab();
  }

  onEdit(automaticReport: AutomaticReport) {
    this.goToAutomaticTab({ edit: automaticReport.id });
  }
}
